import React, {useContext, useEffect} from "react";
import {useLocation} from "react-router-dom";

import solutionImage from "../../../assets/images/solutions/OffGasTreatment.png";
import Breadcrumbs from "../../../components/breadcrumbs";
import CardTechnology from "../../../components/cardTechnology";
import Footer from "../../../components/footer";
import GetInTouch from "../../../components/getInTouch";
import Header from "../../../components/header";
import Questions from "../../../components/questions";
import {GlobalContext} from "../../../context/global/globalContext";
import * as S from "./styles";

function OffGasTreatment() {
  const {setBreadcrumbs} = useContext(GlobalContext);
  const location = useLocation();

  useEffect(() => {
    setBreadcrumbs([
      {label: "Home", path: "/"},
      {label: "Solutions", path: "/solutions"},
      {label: "Off-Gas Treatment", path: location.pathname},
    ]);
  }, [location, setBreadcrumbs]);

  const solutionsData = [
    {
      id: "ActivatedCarbon",
      title: "Activated Carbon",
      description: "High-efficiency air filtration for VOCs, H₂S, and odor removal.",
      link: "/carbon",
    },
    {
      id: "MistEliminators",
      title: "Brink® Mist Eliminators",
      description: "Efficient aerosol removal from gas streams—up to 99,95% efficiency.",
      link: "/brink",
    },
    {
      id: "GasScrubbers",
      title: "Gas Scrubbers",
      description: "Removes acid gases, VOCs, and odors with high efficiency.",
      link: "/gas",
    },
    {
      id: "MeshPad",
      title: "MeshPad",
      description: "Remove mist droplets with low pressure drop and simple operation.",
      link: "/meshpads",
    },
    {
      id: "SAP",
      title: "Sap product",
      description: "Complete solutions for sulphuric acid plants and emissions control.",
      link: "/sap",
    },
  ];

  const questionsData = [
    {
      question: "What makes your off-gas solutions “engineered”?",
      answer: "We tailor the treatment to your gas flow, composition, and site constraints.",
    },
    {
      question: "Can you handle multi-contaminant streams?",
      answer:
        "Yes, we frequently treat complex streams with combined acid gases, VOCs, and particulates.",
    },
    {
      question: "What industries do you serve with this technology?",
      answer: "Metals, chemical, petrochemical, food, and many more.",
    },
  ];

  return (
    <>
      <Header />
      <S.Container>
        <Breadcrumbs />

        <S.MainHeading>Off-Gas Treatment</S.MainHeading>
        <S.Paragraph>
          Ecosystems provides tailored solutions for engineered off-gas treatment across a wide
          range of industries. These systems are designed not only to meet strict emission limits,
          but also to improve energy efficiency, enable raw material recovery, and ensure full
          regulatory compliance.
        </S.Paragraph>
        <S.Paragraph>
          Our approach combines advanced scrubbing technologies with intelligent system
          integration—offering reliable, low-maintenance solutions for complex gas streams. Whether
          it is removing acid gases, organic vapours, or particulate matter, Ecosystems designs each
          installation with performance, safety, and long-term sustainability in mind.
        </S.Paragraph>

        <S.Section>
          <S.SectionTitle>Regulations and Safety</S.SectionTitle>
          <S.SubHeading>Environmental and Regulatory Standards</S.SubHeading>
          <S.List>
            <S.ListItem color="black">
              Industrial Emissions Directive (IED) — Directive 2010/75/EU
            </S.ListItem>
            <S.ListItem color="black">NEC Directive — Directive 2016/2284/EU</S.ListItem>
            <S.ListItem color="black">
              Key Emission Limits — Based on BREF BAT conclusions
            </S.ListItem>
            <S.ListItem color="black">
              Local Compliance — Additional national or site-specific requirements can be integrated
              on request
            </S.ListItem>
          </S.List>

          <S.SubHeading>Health and Safety</S.SubHeading>
          <S.List>
            <S.ListItem color="black">
              All systems are developed with a strong focus on process safety, energy conservation,
              and resource recovery.
            </S.ListItem>
            <S.ListItem color="black">
              Example: Gas-Gas heat exchangers improve energy efficiency and reduce emissions.
            </S.ListItem>
            <S.ListItem color="black">
              Example: Use of H₂O₂ to convert SO₂ into sulphuric acid, eliminating sulphate-rich
              wastewater.
            </S.ListItem>
            <S.ListItem color="black">NH₃, Ammonia Removal</S.ListItem>
          </S.List>
        </S.Section>

        <S.ApplicationsSection>
          <S.ApplicationsList>
            <S.SectionTitle>Applications</S.SectionTitle>
            <S.List>
              <S.ListItem color="blue">Engineering and advisory boards</S.ListItem>
              <S.ListItem color="blue">Research facilities</S.ListItem>
              <S.ListItem color="blue">Third-party systems and OEM integration</S.ListItem>
            </S.List>
          </S.ApplicationsList>
          <S.ImageContainer>
            <img src={solutionImage} alt="Worker next to a flue gas desulfurization equipment" />
          </S.ImageContainer>
        </S.ApplicationsSection>

        <S.TwoColumnSection>
          <S.Column>
            <S.SectionTitle>Process Design</S.SectionTitle>
            <S.List>
              <S.ListItem color="blue">Temperature Range: -40 to 800°C</S.ListItem>
              <S.ListItem color="blue">Flow Rates: Designed for 500 to 800,000 Nm³/h</S.ListItem>
              <S.ListItem color="blue">Operating Pressure: -100 to 1000 mbar</S.ListItem>
              <S.ListItem color="blue">Pressure Drop: 10–80 mbar</S.ListItem>
              <S.ListItem color="blue">Efficiency: up to 99.9% removal efficiency</S.ListItem>
            </S.List>
          </S.Column>
          <S.Column>
            <S.SectionTitle>Materials</S.SectionTitle>
            <S.List>
              <S.ListItem color="blue">Carbon steel</S.ListItem>
              <S.ListItem color="blue">
                Stainless steel (316L, 304L, 904L, high-nickel alloys)
              </S.ListItem>
              <S.ListItem color="blue">FRP, HDPE, and PP</S.ListItem>
              <S.ListItem color="blue">Linings of PP or PTFE in acidic environments</S.ListItem>
            </S.List>
          </S.Column>
        </S.TwoColumnSection>
        <S.CardTitle>Key Technologies</S.CardTitle>
        <CardTechnology data={solutionsData} />
        <Questions items={questionsData} />
        <GetInTouch />
      </S.Container>
      <Footer />
    </>
  );
}

export default OffGasTreatment;
